import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, AlertCircle, Globe, FileText, ChevronDown, ChevronUp, Volume2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGE_LABELS = {
  en: 'English',
  es: 'Espanol (Spanish)',
  pt: 'Portugues (Portuguese)'
};

export const ContractorAgreement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [expandedSections, setExpandedSections] = useState({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signature, setSignature] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAgreement();
  }, []);

  const fetchAgreement = async () => {
    try {
      const token = localStorage.getItem('contractor_token');
      const response = await axios.get(`${API_URL}/api/contractors/agreement`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreement(response.data);
      setSelectedLanguage(response.data.current_language || 'en');
      // Expand all sections by default
      const expanded = {};
      for (let i = 1; i <= 12; i++) {
        expanded[i] = true;
      }
      setExpandedSections(expanded);
    } catch (err) {
      console.error('Error fetching agreement:', err);
      setError('Failed to load agreement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionNum) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionNum]: !prev[sectionNum]
    }));
  };

  const expandAll = () => {
    const expanded = {};
    for (let i = 1; i <= 12; i++) {
      expanded[i] = true;
    }
    setExpandedSections(expanded);
  };

  const collapseAll = () => {
    setExpandedSections({});
  };

  const handleSign = async () => {
    if (!agreedToTerms) {
      toast.error('You must agree to all terms');
      return;
    }
    if (!signature || signature.trim().length < 2) {
      toast.error('Please enter your full name as electronic signature');
      return;
    }

    setSigning(true);
    setError('');

    try {
      const token = localStorage.getItem('contractor_token');
      await axios.post(
        `${API_URL}/api/contractors/agreement/sign`,
        {
          language: selectedLanguage,
          agreed_to_all_terms: true,
          electronic_signature: signature.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Agreement signed successfully!');
      
      // Update local storage
      const contractorInfo = JSON.parse(localStorage.getItem('contractor_info') || '{}');
      contractorInfo.agreement_signed = true;
      localStorage.setItem('contractor_info', JSON.stringify(contractorInfo));
      
      // Navigate to contractor portal
      navigate('/contractor');
    } catch (err) {
      console.error('Error signing agreement:', err);
      setError(err.response?.data?.detail || 'Failed to sign agreement');
      toast.error('Failed to sign agreement');
    } finally {
      setSigning(false);
    }
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = selectedLanguage === 'es' ? 'es-MX' : selectedLanguage === 'pt' ? 'pt-BR' : 'en-US';
      window.speechSynthesis.speak(utterance);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sections = agreement?.sections || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-2xl md:text-3xl font-serif text-white mb-2">
            {sections.title || 'Independent Contractor Agreement'}
          </h1>
          <p className="text-[#C6A87C] text-sm tracking-wider">
            {sections.subtitle || 'Jax Remodeling Solutions LLC'}
          </p>
        </motion.div>

        {/* Language Selector */}
        <div className="bg-white/5 border border-white/10 p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-[#C6A87C]" />
            <span className="text-white text-sm">Select Language:</span>
          </div>
          <div className="flex gap-2">
            {Object.entries(LANGUAGE_LABELS).map(([code, label]) => (
              <button
                key={code}
                onClick={() => setSelectedLanguage(code)}
                className={`px-4 py-2 text-sm transition-colors ${
                  selectedLanguage === code
                    ? 'bg-[#C6A87C] text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="flex justify-end gap-2 mb-4">
          <button
            onClick={expandAll}
            className="text-xs text-gray-400 hover:text-white px-3 py-1 border border-white/10 hover:border-white/30 transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="text-xs text-gray-400 hover:text-white px-3 py-1 border border-white/10 hover:border-white/30 transition-colors"
          >
            Collapse All
          </button>
        </div>

        {/* Agreement Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 border border-white/10 p-6 mb-6"
        >
          {/* Preamble */}
          <div className="mb-8">
            <div className="flex items-start justify-between gap-2">
              <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
                {(sections.preamble || '')
                  .replace('{date}', new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }))
                  .replace('{contractor_name}', agreement?.contractor_name || 'Contractor')}
              </p>
              <button
                onClick={() => speakText(sections.preamble || '')}
                className="text-gray-500 hover:text-[#C6A87C] p-1"
                title="Read aloud"
              >
                <Volume2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Sections */}
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
            <div key={num} className="mb-4 border-b border-white/10 last:border-0 pb-4">
              <button
                onClick={() => toggleSection(num)}
                className="w-full flex items-center justify-between text-left py-2 group"
              >
                <h3 className="text-[#C6A87C] font-medium text-sm md:text-base group-hover:text-white transition-colors">
                  {sections[`section_${num}_title`] || `Section ${num}`}
                </h3>
                {expandedSections[num] ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {expandedSections[num] && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-2"
                >
                  <div className="flex items-start gap-2">
                    <p className="text-gray-400 text-xs md:text-sm leading-relaxed whitespace-pre-line flex-1">
                      {sections[`section_${num}_content`] || ''}
                    </p>
                    <button
                      onClick={() => speakText(sections[`section_${num}_content`] || '')}
                      className="text-gray-500 hover:text-[#C6A87C] p-1 flex-shrink-0"
                      title="Read aloud"
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          ))}
        </motion.div>

        {/* Acknowledgment Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white/5 border border-white/10 p-6 mb-6"
        >
          <h3 className="text-[#C6A87C] font-medium mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {sections.acknowledgment_title || 'Acknowledgment and Agreement'}
          </h3>
          <p className="text-gray-400 text-sm leading-relaxed whitespace-pre-line mb-6">
            {sections.acknowledgment_content || ''}
          </p>

          {/* Agreement Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer group mb-6">
            <div
              className={`w-6 h-6 border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                agreedToTerms
                  ? 'bg-[#C6A87C] border-[#C6A87C]'
                  : 'border-gray-500 group-hover:border-[#C6A87C]'
              }`}
              onClick={() => setAgreedToTerms(!agreedToTerms)}
            >
              {agreedToTerms && <Check className="w-4 h-4 text-white" />}
            </div>
            <span className="text-white text-sm">
              I have read, understood, and agree to ALL terms and conditions in this Independent Contractor Agreement. 
              I understand this is a legally binding document.
            </span>
          </label>

          {/* Signature Field */}
          <div className="space-y-3">
            <label className="block text-gray-400 text-xs uppercase tracking-wider">
              {sections.signature_contractor || 'Electronic Signature'} (Type your full name)
            </label>
            <input
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Type your full legal name"
              className="w-full bg-white/5 border border-white/20 text-white px-4 py-3 focus:border-[#C6A87C] focus:outline-none transition-colors font-serif italic text-lg"
            />
            <p className="text-gray-500 text-xs">
              By typing your name above, you are signing this agreement electronically. 
              This electronic signature has the same legal effect as a handwritten signature.
            </p>
          </div>
        </motion.div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 mb-6 flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Contractor Info */}
        <div className="bg-white/5 border border-white/10 p-4 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">{sections.contractor_name_label || 'Contractor Name'}:</span>
              <span className="text-white ml-2">{agreement?.contractor_name}</span>
            </div>
            <div>
              <span className="text-gray-500">{sections.contractor_id_label || 'Contractor ID'}:</span>
              <span className="text-[#C6A87C] ml-2 font-mono">{agreement?.contractor_id}</span>
            </div>
          </div>
        </div>

        {/* Sign Button */}
        <button
          onClick={handleSign}
          disabled={signing || !agreedToTerms || !signature.trim()}
          className={`w-full py-4 text-sm font-semibold uppercase tracking-widest transition-colors flex items-center justify-center gap-2 ${
            agreedToTerms && signature.trim()
              ? 'bg-[#C6A87C] text-white hover:bg-[#B09265]'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          {signing ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Signing Agreement...
            </>
          ) : (
            <>
              <FileText className="w-5 h-5" />
              Sign Agreement
            </>
          )}
        </button>

        <p className="text-gray-600 text-xs text-center mt-4">
          A copy of this signed agreement will be saved in English and your selected language.
        </p>
      </div>
    </div>
  );
};

export default ContractorAgreement;
